/**
 * Lightweight i18n runtime (no next-intl / i18next — see AGENTS.md / frontend guide).
 * English (`en`) is the system default and ultimate fallback.
 */

export const SUPPORTED_LOCALES = ["en", "zh-CN"] as const;
export type AppLocale = (typeof SUPPORTED_LOCALES)[number];
export const DEFAULT_LOCALE: AppLocale = "en";

export const LOCALE_STORAGE_KEY = "lwp.locale";
export const LOCALE_COOKIE_KEY = "lwp_locale";

export function isAppLocale(value: unknown): value is AppLocale {
  return value === "en" || value === "zh-CN";
}

/** Normalize any stored/browser value to a supported locale; invalid → `en`. */
export function resolveLocale(value: unknown): AppLocale {
  if (typeof value !== "string") return DEFAULT_LOCALE;
  const trimmed = value.trim();
  if (isAppLocale(trimmed)) return trimmed;
  const lower = trimmed.toLowerCase().replace(/_/g, "-");
  if (lower === "zh" || lower.startsWith("zh-cn") || lower.startsWith("zh-hans")) {
    return "zh-CN";
  }
  if (lower === "en" || lower.startsWith("en-")) return "en";
  return DEFAULT_LOCALE;
}

/**
 * Precedence: valid saved preference → browser/Accept-Language → English.
 * Invalid saved values are ignored (never force-fallback mid-chain incorrectly).
 */
export function detectLocale(options?: {
  saved?: string | null;
  acceptLanguage?: string | null;
  navigatorLanguage?: string | null;
}): AppLocale {
  if (options?.saved != null && options.saved !== "") {
    const trimmed = options.saved.trim();
    if (isAppLocale(trimmed)) return trimmed;
  }

  const candidates: string[] = [];
  if (options?.navigatorLanguage) candidates.push(options.navigatorLanguage);
  if (options?.acceptLanguage) {
    for (const part of options.acceptLanguage.split(",")) {
      const tag = part.split(";")[0]?.trim();
      if (tag) candidates.push(tag);
    }
  }

  for (const candidate of candidates) {
    const lower = candidate.toLowerCase().replace(/_/g, "-");
    if (lower.startsWith("zh")) return "zh-CN";
    if (lower.startsWith("en")) return "en";
  }

  return DEFAULT_LOCALE;
}

export function readStoredLocale(): AppLocale | null {
  if (typeof window === "undefined") return null;
  try {
    const fromStorage = window.localStorage.getItem(LOCALE_STORAGE_KEY);
    if (fromStorage && isAppLocale(fromStorage)) return fromStorage;
  } catch {
    /* ignore */
  }
  const match = document.cookie.match(
    new RegExp(`(?:^|; )${LOCALE_COOKIE_KEY}=([^;]*)`),
  );
  if (match?.[1]) {
    const decoded = decodeURIComponent(match[1]);
    if (isAppLocale(decoded)) return decoded;
  }
  return null;
}

export function persistLocale(locale: AppLocale): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    /* ignore */
  }
  const maxAge = 60 * 60 * 24 * 365;
  document.cookie = `${LOCALE_COOKIE_KEY}=${encodeURIComponent(locale)};path=/;max-age=${maxAge};SameSite=Lax`;
}

/** Replace `{name}` placeholders. Never concatenates translated fragments. */
export function interpolate(
  template: string,
  vars?: Record<string, string | number | null | undefined>,
): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (_, key: string) => {
    const value = vars[key];
    return value == null ? "" : String(value);
  });
}

export function localeToBcp47(locale: AppLocale): string {
  return locale === "zh-CN" ? "zh-CN" : "en-US";
}

export function formatDateTime(
  value: string | number | Date | null | undefined,
  locale: AppLocale,
  timeZone?: string,
): string {
  if (value == null || value === "") return "";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat(localeToBcp47(locale), {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: timeZone || undefined,
  }).format(date);
}

export function formatDate(
  value: string | number | Date | null | undefined,
  locale: AppLocale,
  timeZone?: string,
): string {
  if (value == null || value === "") return "";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat(localeToBcp47(locale), {
    dateStyle: "medium",
    timeZone: timeZone || undefined,
  }).format(date);
}

export function formatNumber(
  value: number,
  locale: AppLocale,
  options?: Intl.NumberFormatOptions,
): string {
  return new Intl.NumberFormat(localeToBcp47(locale), options).format(value);
}

export function formatCurrency(
  amountMinor: number,
  currencyCode: string,
  locale: AppLocale,
): string {
  const major = amountMinor / 100;
  return new Intl.NumberFormat(localeToBcp47(locale), {
    style: "currency",
    currency: currencyCode,
  }).format(major);
}

/** Deep-merge overlay onto base; missing overlay leaves leave English. */
export function deepMergeMessages<T extends Record<string, unknown>>(
  base: T,
  overlay: Record<string, unknown> | null | undefined,
): T {
  if (!overlay) return base;
  const out: Record<string, unknown> = {
    ...(base as Record<string, unknown>),
  };
  for (const [key, value] of Object.entries(overlay)) {
    const baseVal = (base as Record<string, unknown>)[key];
    if (
      value &&
      typeof value === "object" &&
      !Array.isArray(value) &&
      baseVal &&
      typeof baseVal === "object" &&
      !Array.isArray(baseVal)
    ) {
      out[key] = deepMergeMessages(
        baseVal as Record<string, unknown>,
        value as Record<string, unknown>,
      );
    } else if (value !== undefined) {
      out[key] = value;
    }
  }
  return out as T;
}
